import { API, Logger } from 'aws-amplify';
import { byCreatedMonth } from '@/graphql/queries';
import { format, subMonths, eachMonthOfInterval, parseISO, parse, compareAsc } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

const logger = new Logger('medicalRecords');

/**
 * 診療記録一覧
 * @type {{mutations: {}, state: {}, getters: {}, actions: {}, namespaced: boolean}}
 */
const medicalRecords = {
  namespaced: true,
  state: {
    // 診療記録一覧
    records: [],
  },
  getters: {
    records: state => state.records,
  },
  mutations: {
    /**
     * 診療記録一覧に設定する
     * @param state
     * @param payload
     */
    setMedicalRecords: (state, payload) => {
      state.records = payload.map(item => {
        return {
          // key
          id: item.id,
          treatmentConsultationSeq: item.treatmentConsultationSeq,
          treatmentSerialNumber: item.treatmentSerialNumber,
          userId: item.userId,
          // 事前問診
          userInfo: JSON.parse(item.userInfo),
          riskOfSerious: JSON.parse(item.riskOfSerious),
          interview: JSON.parse(item.interview),
          travelHistory: JSON.parse(item.travelHistory),
          intensiveContact: JSON.parse(item.intensiveContact),
          statusCode: item.statusCode,
          // 診療記録
          recordTime: JSON.parse(item.recordTime),
          recordConsultation: JSON.parse(item.recordConsultation),
          recordAssessment: JSON.parse(item.recordAssessment),
          recordPlan: JSON.parse(item.recordPlan),
          // JST変換
          createdAt: convertUtcToJst(item.createdAt, 'yyyy-MM-dd HH:mm:ss'),
          updatedAt: convertUtcToJst(item.updatedAt, 'yyyy-MM-dd HH:mm:ss'),
        };
      });
    },
  },
  actions: {
    /**
     * 診療記録一覧取得
     * @param commit
     * @param payload
     * @returns {Promise<void>}
     */
    getMedicalRecords: async ({ commit }, payload) => {
      logger.info('getMedicalRecords', payload);
      try {
        // todo: 暫定で直近の半年間を対象とする
        // 後々は、入力させる想定
        // sub: 5は5ヶ月前まで。+1(今月)で半年となる
        const months = eachMonthOfInterval({ start: subMonths(new Date(), 5), end: new Date() }).
          map(x => format(x, 'yyyy-MM'));

        const results = await getMedicalRecordsByMonthsApi(months);

        // 最終的なソート
        // createdMonth=>createdAt ASC
        results.sort((a, b) => {
          // createdMonthが同じ場合は、createdAtでソートする
          if (a.createdMonth === b.createdMonth) {
            return compareAsc(parseISO(a.createdAt), parseISO(b.createdAt));
          }
          // 上記以外はcreatedMonthでソートする
          return compareAsc(parse(a.createdMonth, 'yyyy-MM', new Date()), parse(b.createdMonth, 'yyyy-MM', new Date()));
        });

        logger.info('getMedicalRecords.results', results);
        commit('setMedicalRecords', results);
      } catch (e) {
        logger.error('getMedicalRecords.exception', e);
        throw e;
      }
    },
  },
};

/**
 * 指定の年月の診療記録一覧をすべて取得する
 * @param months
 * @returns {Promise<*[]>}
 */
const getMedicalRecordsByMonthsApi = async (months) => {
  try {
    let results = [];
    let first = true;
    let nextApiVariables = [];

    // nextTokenがなくなるまで繰り返す
    while (first || nextApiVariables.length > 0) {
      const apiVariables = (first) ? months.map(x => {
        return {
          createdMonth: x,
          sortDirection: 'ASC',
        };
      }) : nextApiVariables;

      // 初回判定OFF
      first = false;

      // API呼び出しPromiseの配列
      const apis = apiVariables.map(x => {
        return API.graphql({
          query: byCreatedMonth,
          variables: x,
          authMode: 'AWS_IAM',
        }).then(response => {
          // nextTokenがある場合に次のAPI呼びだし変数に保持する
          if (response.data.byCreatedMonth.nextToken) {
            logger.info('getMedicalRecords.nextToken', response);
            let variable = x;
            variable['nextToken'] = response.data.byCreatedMonth.nextToken;
            nextApiVariables.push(variable);
          }
          return response;
        });
      });
      logger.info('getMedicalRecords.apis', apis);

      // 初期化しておく
      nextApiVariables = [];

      // 終了待ち
      const result = await Promise.all(apis).then(values => {
        logger.info('values', values);
        // 結果をマージ
        let list = [];
        values.forEach(x => {
          list = list.concat(x.data.byCreatedMonth.items);
        });
        return list;
      });
      // 結果配列にマージ
      results = results.concat(result);
    }

    logger.info('getMedicalRecords.results', results);
    return results;
  } catch (e) {
    logger.error('getMedicalRecords.exception', e);
    throw e;
  }
};

/**
 * UTC文字列をJST Date Objectに変換する
 * @param strDate
 * @param formatStr
 * @returns {string}
 */
const convertUtcToJst = (strDate, formatStr) => {
  const utcDate = parseISO(strDate);
  const jstDate = utcToZonedTime(utcDate, 'Asia/Tokyo');
  return format(jstDate, formatStr);
};

export default medicalRecords;
